import api from '../utils/api/api.service';
import errorHelper from '../utils/api/error-helper.service';
import TicketMapper from '../../mappers/ticket.mapper';
import TicketStatsMapper from '../../mappers/ticket-stats.mapper';
import { TicketStatsPeriod } from '../../models/ticket-stats.model';
import { TicketStatsUiModel } from '../../ui-models/ticket-stats.ui-model';
import {
  CreateTicketPayload,
  TicketAttachmentPayload,
  TicketCategoriesModel,
  TicketPriority,
  TicketView
} from '../../models/ticket.model';
import {
  TicketDetailUiModel,
  TicketReservationContextUiModel,
  TicketUiModel
} from '../../ui-models/ticket.ui-model';
import { ErrorInfo } from '../../interfaces/error-info.interface';

/**
 * Tickets (Trello #244). In the platform layer rather than inside TicketsModule
 * because tickets are raised from other modules too: the reservation and unit
 * type pages both offer "Raise ticket".
 *
 * A 403 here is Zammad's answer, not ours: the integrator acts AS the caller, and
 * a person who handed a ticket to another team keeps only `create` on it.
 */
const MODULE_NAME = 'ticket';

/** Same shape as lead.data.ts: one loosely typed read of the error body. */
const fail = (response: unknown, fn: unknown) => {
  const res = response as { status?: number; data?: { errorCode?: string }; problem?: unknown };
  return errorHelper(
    Number(res.status),
    res.data && res.data.errorCode ? String(res.data.errorCode || '') : String(res.problem || ''),
    MODULE_NAME,
    (fn as { displayName?: string }).displayName as string
  );
};

/** The taxonomy the create form is built from. Fetched, so it never drifts from the seed. */
export function getTicketCategories(): Promise<TicketCategoriesModel | ErrorInfo> {
  return api.getTicketCategories().then((response) => {
    if (response.ok && response.data && response.data.data) return response.data.data;
    return fail(response, api.getTicketCategories);
  });
}

export function getTickets(view: TicketView, page = 1): Promise<TicketUiModel[] | ErrorInfo> {
  return api.getTickets(view, page).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TicketMapper.mapToUIList(response.data.data);
    }
    return fail(response, api.getTickets);
  });
}

export function getTicket(id: number): Promise<TicketDetailUiModel | ErrorInfo> {
  return api.getTicket(id).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TicketMapper.mapDetailToUI(response.data.data);
    }
    return fail(response, api.getTicket);
  });
}

export function createTicket(payload: CreateTicketPayload): Promise<TicketUiModel | ErrorInfo> {
  return api.createTicket(payload).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TicketMapper.mapToUI(response.data.data);
    }
    return fail(response, api.createTicket);
  });
}

export function addTicketNote(
  id: number,
  body: string,
  attachments?: TicketAttachmentPayload[]
): Promise<TicketDetailUiModel | ErrorInfo> {
  return api.addTicketNote(id, body, attachments).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TicketMapper.mapDetailToUI(response.data.data);
    }
    return fail(response, api.addTicketNote);
  });
}

/** Emails the ticket's guest. The result is the ticket with its thread. */
export function replyToTicket(
  id: number,
  body: string,
  attachments?: TicketAttachmentPayload[]
): Promise<TicketDetailUiModel | ErrorInfo> {
  return api.replyToTicket(id, body, attachments).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TicketMapper.mapDetailToUI(response.data.data);
    }
    return fail(response, api.replyToTicket);
  });
}

/**
 * Gives an emailed-in ticket its category, which routes it to that category's first
 * team, like a new ticket. As with a handoff, the caller may then no longer be able
 * to open it, so the result is the ticket as the change left it.
 */
export function categorizeTicket(
  id: number,
  payload: { code: string; emergency?: boolean }
): Promise<TicketUiModel | ErrorInfo> {
  return api.categorizeTicket(id, payload).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TicketMapper.mapToUI(response.data.data);
    }
    return fail(response, api.categorizeTicket);
  });
}

/**
 * Moves the ticket to the next team in its owner chain. The result is the
 * ticket as the handoff left it. Re-reading it afterwards will usually be a
 * 403, because the caller's team no longer owns it.
 */
export function handoffTicket(
  id: number,
  payload: { team?: string; reason?: string }
): Promise<TicketUiModel | ErrorInfo> {
  return api.handoffTicket(id, payload).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TicketMapper.mapToUI(response.data.data);
    }
    return fail(response, api.handoffTicket);
  });
}

export function raiseTicketPriority(
  id: number,
  priority: TicketPriority
): Promise<TicketUiModel | ErrorInfo> {
  return api.raiseTicketPriority(id, priority).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TicketMapper.mapToUI(response.data.data);
    }
    return fail(response, api.raiseTicketPriority);
  });
}

export function assignTicket(id: number, to: 'me' | number): Promise<TicketUiModel | ErrorInfo> {
  return api.assignTicket(id, to).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TicketMapper.mapToUI(response.data.data);
    }
    return fail(response, api.assignTicket);
  });
}

export function setTicketState(
  id: number,
  state: 'open' | 'closed'
): Promise<TicketUiModel | ErrorInfo> {
  return api.setTicketState(id, state).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TicketMapper.mapToUI(response.data.data);
    }
    return fail(response, api.setTicketState);
  });
}

export function downloadTicketAttachment(
  ticketId: number,
  articleId: number,
  attachmentId: number
): Promise<Blob | ErrorInfo> {
  return api.downloadTicketAttachment(ticketId, articleId, attachmentId).then((response) => {
    if (response.ok && response.data) return response.data;
    return fail(response, api.downloadTicketAttachment);
  });
}

/**
 * The reservation a ticket refers to, for the context panel. Read with the caller's
 * own reservation access, so someone without it sees the reference, not the guest.
 */
export function getTicketReservationContext(
  reservationId: string
): Promise<TicketReservationContextUiModel | ErrorInfo> {
  return api.getOneReservation(reservationId).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TicketMapper.mapReservationToContext(response.data.data);
    }
    return fail(response, api.getOneReservation);
  });
}

/** The performance dashboard. A 403 means no helpdesk account or no team; 503 = not configured. */
export function getTicketStats(
  period: TicketStatsPeriod,
  team?: number
): Promise<TicketStatsUiModel | ErrorInfo> {
  return api.getTicketStats(period, team).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return TicketStatsMapper.mapToUI(response.data.data);
    }
    return fail(response, api.getTicketStats);
  });
}

/** Fire and forget: a missed heartbeat only means the next one counts. */
export function sendPresence(): void {
  api.sendPresence().catch(() => undefined);
}
